import * as Sentry from "@sentry/browser";
import {
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  signIn,
  signOut as signOutAmplify,
} from "aws-amplify/auth";
import { signOut } from "next-auth/client";
import { useRouter } from "next/router";
import React from "react";
import toast from "react-hot-toast";
import "../src/configureAmplify";
import { ONBOARDING_INFO } from "../src/constansts/localStorage";
import { useProfitWell } from "../src/hooks/useProfitWell";
import { MixpanelTracking } from "../src/services/MixpanelService";

interface AuthContextValue {
  userAttributes: FetchUserAttributesOutput | null;
  authedUser: AuthUser | null;
  login: (payload: { username: string; password: string }) => Promise<void>;
  logout: () => void;
  [key: string]: any;
  authedUserLoading: boolean;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = React.createContext<AuthContextValue>({
  userAttributes: {},
  authedUser: null,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  authedUserLoading: true,
});

function AuthProvider(props: AuthProviderProps) {
  const { children } = props;
  const [authedUser, setAuthedUser] = React.useState<AuthUser | null>(null);
  const [userAttributes, setUserAttributes] = React.useState<FetchUserAttributesOutput | null>(
    null,
  );
  const router = useRouter();
  const [authedUserLoading, setAuthedUserLoading] = React.useState<boolean>(true);

  Sentry.setUser({ email: userAttributes?.email });

  // Function to check if token is expired or about to expire (within 5 minutes)
  const isTokenExpired = React.useCallback((session: any) => {
    if (!session?.tokens?.accessToken?.payload?.exp) return false;

    const expirationTime = session.tokens.accessToken.payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000;

    // Return true if token expires within 5 minutes or has already expired
    return currentTime + fiveMinutesInMs >= expirationTime;
  }, []);

  // Function to refresh the authentication session
  const refreshSession = React.useCallback(async () => {
    try {
      console.log("Refreshing auth session");
      // In Amplify v6, fetchAuthSession will automatically handle token refresh
      // when tokens are expired if a valid refresh token exists
      const refreshedSession = await fetchAuthSession({ forceRefresh: true });

      if (refreshedSession && refreshedSession.tokens) {
        // Update user data after successful refresh
        const user = await getCurrentUser();
        setAuthedUser(user);
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to refresh auth session:", error);
      return false;
    }
  }, []);

  // Initial auth check
  React.useEffect(() => {
    setAuthedUserLoading(true);
    fetchAuthSession()
      .then(async (session) => {
        if (session && session.tokens) {
          // Check if token is expired or about to expire
          if (isTokenExpired(session)) {
            await refreshSession();
          } else {
            getCurrentUser().then((user) => {
              setAuthedUser(user);
            });

            fetchUserAttributes().then((attributes) => {
              setUserAttributes(attributes);
            });
          }
        }
      })
      .finally(() => {
        setAuthedUserLoading(false);
      });
  }, [isTokenExpired, refreshSession]);

  // Set up periodic token refresh check
  React.useEffect(() => {
    // Only set up refresh timer if user is authenticated
    if (!authedUser) return;

    // Check every minute if token needs to be refreshed
    const refreshTimer = setInterval(async () => {
      const session = await fetchAuthSession();
      if (isTokenExpired(session)) {
        await refreshSession();
      }
    }, 60000); // Check every minute

    // Cleanup timer on unmount
    return () => clearInterval(refreshTimer);
  }, [authedUser, isTokenExpired, refreshSession]);

  const userEmail = React.useMemo(() => userAttributes?.email, [userAttributes?.email]);
  const userSub = React.useMemo(() => userAttributes?.sub, [userAttributes?.sub]);

  React.useEffect(() => {
    if (userSub) {
      MixpanelTracking.getInstance().identifyNewUser(userSub);
    }
  }, [authedUser, userSub]);

  useProfitWell(userEmail);

  const login = React.useCallback(
    async (payload: { username: string; password: string }): Promise<void> => {
      try {
        const { isSignedIn, nextStep } = await signIn(payload);
        if (isSignedIn) {
          const user = await getCurrentUser();
          setAuthedUser(user);
          const attributes = await fetchUserAttributes();
          setUserAttributes(attributes);
          toast.success("Login successful!");
          await router.push({
            pathname: "/",
            query: { ...router.query },
          });
        }
        if (!isSignedIn && nextStep.signInStep === "CONFIRM_SIGN_UP") {
          await router.push({
            pathname: "/register",
            query: {
              ...router.query,
              isVerify: true,
              username: payload.username,
            },
          });
        }
      } catch (e) {
        toast.error("Incorrect email or password!");
      }
    },
    [router],
  );

  const logout = React.useCallback(async () => {
    await signOutAmplify();
    setAuthedUser(null);
    localStorage.removeItem("store-id");
    localStorage.removeItem(ONBOARDING_INFO);
    await router.push({
      pathname: "/login",
      query: router.query,
    });
    await signOut();
    MixpanelTracking.getInstance().resetSession();
  }, [router]);

  const value = React.useMemo(
    () => ({
      login,
      logout,
      userAttributes,
      authedUser,
      authedUserLoading,
    }),
    [login, logout, userAttributes, authedUser, authedUserLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuthContext() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
}

export { AuthContext, AuthProvider, useAuthContext };
